import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { useSelectedGamesInModuleQuery } from "entities/game/queries/useSelectedGamesInModuleQuery";
import { Game } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { useModuleSelectedStudiosQuery } from "entities/modules/queries/useModuleSelectedStudiosQuery";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { GamesOrderType, ModuleGame } from "entities/modules/types";
import { useStudiosListQuery } from "entities/studios/queries/useStudiosListQuery";
import { useTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useThemeTagsListQuery } from "entities/themeTags/queries/useThemeTagListQuery";
import { sortBy } from "lodash";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { createContext } from "shared/utils/context";
import {
  getAlgoliaTagsFilters,
  serializeModuleTagsToBlocks,
} from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useSettingsModules } from "../../../context";
import { useGeneralData } from "../stores/useGeneralData";
import { useStudioOrderingData } from "../stores/useStudioOrderingData";
import { useStudiosData } from "../stores/useStudiosData";
import { ModuleDetailsTabs, ModuleDetailsType } from "./types";
import { formatStudios, formatSelectedGames } from "./utils";

const [Provider, useSettingModuleDetails] = createContext<ModuleDetailsType>({
  name: "SettingModuleDetailsContext",
  hookName: "useSettingModuleDetails",
  providerName: "SettingModuleDetailsProvider",
});

const SettingModuleDetailsProvider: FC<PropsWithChildren> = (props) => {
  const { id, tabDetailsId: tabDetails } = useParams();
  const brandId = String(id);
  const settingId = String(tabDetails);
  const { moduleDetailsDrawer } = useSettingsModules();
  const data = moduleDetailsDrawer.drawerDetails;
  const moduleId = (data?.id || data || null) as string | null;
  const [selectedTab, setSelectedTab] = useState(ModuleDetailsTabs.General);
  const initialRenderRef = useRef(false);

  const { allowed: hasReadAccess } = useRolePermission("brandsAndPages:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:read",
  );

  const { tableData: allGames, isLoading: isLoadingAllGames } = useGamesData({
    isActive: ["true"],
    size: 1000,
  });
  const { data: tags = [], isLoading: isLoadingTags } = useTagsListQuery();
  const { data: metadataTags = [], isLoading: isLoadingMetadataTags } =
    useMetadataTagsListQuery();
  const { data: themeTags = [], isLoading: isLoadingThemeTags } =
    useThemeTagsListQuery();
  const { data: studios, isLoading: isLoadingStudios } = useStudiosListQuery();

  const {
    setModuleName,
    setAdminNotes,
    setTranslations,
    setModuleType,
    setModuleLayout,
    setStudioFilterEnabled,
    clearGeneralData,
    setBaseUrl,
    setContentBlocks,
    setBonusObtainType,
    setRows,
    setIconShown,
    setIcon,
  } = useGeneralData(
    useShallow((state) => ({
      contentBlocks: state.contentBlocks,
      moduleLayout: state.moduleLayout,
      setModuleName: state.setModuleName,
      setAdminNotes: state.setAdminNotes,
      setTranslations: state.setTranslations,
      setModuleType: state.setModuleType,
      setRows: state.setRows,
      setModuleLayout: state.setModuleLayout,
      setStudioFilterEnabled: state.setStudioFilterEnabled,
      setBaseUrl: state.setBaseUrl,
      setContentBlocks: state.setContentBlocks,
      setBonusObtainType: state.setBonusObtainType,
      clearGeneralData: state.clearGeneralData,
      setOneCardBlockImage: state.setOneCardBlockImage,
      setThreeCardBlockImages: state.setThreeCardBlockImages,
      setIconShown: state.setIconShown,
      setIcon: state.setIcon,
      moduleType: state.moduleType,
    })),
  );

  const {
    orderType,
    selectedGames,
    manualSelectedGames,
    tagSelectedGames,
    setOrderType,
    setSelectedGames,
    setTagSelectedGames,
    setManualSelectedGames,
    clearSelectedGamesData,
  } = useSelectedGamesData(
    useShallow((state) => ({
      orderType: state.orderType,
      selectedGames: state.selectedGames,
      manualSelectedGames: state.manualSelectedGames,
      tagSelectedGames: state.tagSelectedGames,
      setOrderType: state.setOrderType,
      setSelectedGames: state.setSelectedGames,
      setManualSelectedGames: state.setManualSelectedGames,
      setTagSelectedGames: state.setTagSelectedGames,
      clearSelectedGamesData: state.clearSelectedGamesData,
    })),
  );

  const { setSelectedStudios, clearStudiosData } = useStudiosData(
    useShallow((state) => ({
      setSelectedStudios: state.setSelectedStudios,
      clearStudiosData: state.clearStudiosData,
    })),
  );

  const { tagBlocks, setTagBlocks, clearTagsData } = useTagBlocksStore(
    useShallow((state) => ({
      tagBlocks: state.tagBlocks,
      setTagBlocks: state.setTagBlocks,
      clearTagsData: state.clearTagsData,
    })),
  );

  const { setOrderedStudios, clearStudioOrderingData } = useStudioOrderingData(
    useShallow((state) => ({
      setOrderedStudios: state.setOrderedStudios,
      clearStudioOrderingData: state.clearStudioOrderingData,
    })),
  );

  const {
    data: selectedGamesList,
    isLoading: isLoadingSelectedGamesList,
    isFetching: isFetchingSelectedGamesList,
  } = useSelectedGamesInModuleQuery({
    moduleId,
    filters: getAlgoliaTagsFilters(tagBlocks),
  });

  const studioIds = useMemo(() => {
    const studiosIds = new Set<number>();
    selectedGames.forEach((game) => {
      if (game.studioId) {
        studiosIds.add(game.studioId);
      }
    });

    return Array.from(studiosIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGames.length]);

  const { data: availableStudios, isLoading: isLoadingAvailableStudios } =
    useModuleSelectedStudiosQuery(studioIds);

  useEffect(() => {
    if (!data) {
      console.warn("Cannot find setting module");
      return;
    }
    const orderType = data.studioFilterEnabled
      ? GamesOrderType.Manual
      : data.gamesOrderType || GamesOrderType.Random;
    setModuleName(data.name);
    setAdminNotes(data.adminNotes ?? "");
    setTranslations(data.translations ?? {});
    setModuleType(data.type);
    setModuleLayout(data.layout);
    setOrderType(orderType);
    setSelectedStudios(formatStudios(data, studios));
    setStudioFilterEnabled(data.studioFilterEnabled ?? false);
    setBaseUrl(data.baseUrl ?? "");
    setRows(data.rows ?? 1);
    setContentBlocks(data.contentBlocks);
    setBonusObtainType(data.bonusObtainType ?? null);

    const tagBlocks = serializeModuleTagsToBlocks({
      moduleTagTypes: data.tagTypes,
      moduleTags: data.tags,
      tags: tags,
      metadataTags: metadataTags,
      themeTags: themeTags,
      tagsMatchRules: data.tagsMatchRules ?? [],
    });

    setTagBlocks(tagBlocks);

    if (data.icon) {
      setIconShown(true);
      setIcon(data.icon);
    }

    // disabled, because we need to track only for loading state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingTags,
    isLoadingMetadataTags,
    isLoadingThemeTags,
    isLoadingStudios,
    moduleId,
  ]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(ModuleDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, data]);

  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingAllGames && data?.selectedGames.length && !!availableStudios;

    if (shouldUpdateGames) {
      const manualGames = data.selectedGames
        .map((selectedGame: ModuleGame) =>
          allGames.data.find((game) => game.id.toString() === selectedGame.id),
        )
        .filter(Boolean) as Game[];

      const formattedGames = formatSelectedGames(
        manualGames,
        GamesOrderType.Manual,
        availableStudios,
        data?.selectedGames,
      );

      if (!manualSelectedGames.length && !initialRenderRef.current) {
        setManualSelectedGames(formattedGames);
        initialRenderRef.current = true;
      } else if (manualSelectedGames.length) {
        const orderFromModule = formattedGames.map((g) => g.id);
        const orderFromState = manualSelectedGames.map((g) => g.id);
        const orderChanged =
          JSON.stringify(orderFromModule) !== JSON.stringify(orderFromState);

        if (orderChanged) {
          setManualSelectedGames(formattedGames);
        } else {
          const updatedGames = manualSelectedGames.map((game) => {
            const formattedGame = formattedGames.find(
              (formattedGame) => formattedGame.id === game.id,
            );

            if (formattedGame && formattedGame.studioName) {
              return {
                ...game,
                studioName: formattedGame.studioName,
              };
            }
            return game;
          });

          setManualSelectedGames(updatedGames);
        }
      }
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingAllGames,
    data?.selectedGames,
    availableStudios?.length,
    initialRenderRef.current,
  ]);

  useEffect(() => {
    if (!manualSelectedGames.length || !availableStudios) return;

    const formattedGames = formatSelectedGames(
      manualSelectedGames
        .map((game) => {
          return allGames.data.find((g) => g.id.toString() === game.id);
        })
        .filter(Boolean) as Game[],
      orderType,
      availableStudios,
      data?.selectedGames,
    );

    setManualSelectedGames(formattedGames);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingSelectedGamesList && selectedGamesList && !!availableStudios;

    if (shouldUpdateGames) {
      const sortedGames = sortBy(selectedGamesList, (game) => {
        const existingIdx = data?.selectedGames.findIndex(
          (selectedGame: { id: string }) =>
            game.id.toString() === selectedGame.id,
        );

        return existingIdx === -1 ? Infinity : existingIdx;
      });

      const formattedGames = formatSelectedGames(
        sortedGames,
        orderType,
        availableStudios,
        data?.selectedGames,
      );

      setTagSelectedGames(formattedGames);
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderType,
    isLoadingSelectedGamesList,
    selectedGamesList,
    data?.selectedGames,
    availableStudios?.length,
  ]);

  useEffect(() => {
    const combinedGames = [...manualSelectedGames, ...tagSelectedGames];

    const uniqueGames = combinedGames.filter(
      (game, index, self) => index === self.findIndex((g) => g.id === game.id),
    );

    setSelectedGames(uniqueGames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualSelectedGames, tagSelectedGames]);

  useEffect(() => {
    if (!availableStudios || isLoadingAvailableStudios) {
      return;
    }

    const sortedStudios = sortBy(availableStudios, (studio) => {
      const existingIdx = data?.orderedGamesStudiosIds?.findIndex(
        (studioId: string) => {
          return studio.id.toString() === studioId;
        },
      );

      return existingIdx === -1 ? Infinity : existingIdx;
    });

    const formattedStudios = sortedStudios.map((studio, idx) => {
      const selectedGamesCount = selectedGames.filter(
        (game) => game.studioId === studio.id,
      ).length;

      return {
        id: studio.id.toString(),
        name: studio.name,
        gamesCount: selectedGamesCount,
        order: idx + 1,
      };
    });

    setOrderedStudios(formattedStudios);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableStudios,
    isLoadingAvailableStudios,
    data?.orderedGamesStudiosIds,
    selectedGames,
  ]);

  const handleDrawerClose = () => {
    moduleDetailsDrawer.closeDrawer();
    setSelectedTab(ModuleDetailsTabs.General);
    clearGeneralData();
    clearSelectedGamesData();
    clearStudiosData();
    clearTagsData();
    clearStudioOrderingData();
    initialRenderRef.current = false;
  };

  const value: ModuleDetailsType = {
    moduleId,
    handleDrawerClose,
    selectedTab,
    setSelectedTab,
    moduleData: data ?? null,
    isLoadingSelectedGamesList,
    isFetchingSelectedGamesList,
    isLoadingAvailableStudios,
    brandId,
    settingId,
    availableStudios,
  };

  return <Provider value={value}>{props.children}</Provider>;
};

export { SettingModuleDetailsProvider, useSettingModuleDetails };
